// Squadron radio chatter (roadmap #22). Bots and combat generate situational
// lines — a contact spotted, a hit taken, a fire, a kill, vengeance, the bell —
// and this pipes them into the existing team-chat log so the sky feels crewed.
// The server only ever sends a tiny kind string (E="radio"); every word lives
// here, chosen deterministically so replays and spectators read the same lines.

// A stable 32-bit hash of a string, used to pick phrase variants and a speaker
// deterministically (so a given pilot tends to reuse their own phrasings).
function hash(str) {
  let h = 2166136261 >>> 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// Phrase tables keyed by kind. %N is filled with a teammate/target name. A pilot
// picks variants by a hash of (name+kind) plus how many times they've said it,
// so each tends to reuse their own catchphrases rather than reading at random.
const PHRASES = {
  contact: ["contact, coming around", "tally, engaging", "got one, going in", "bandit, on him", "eyes on, attacking"],
  hit: ["I'm hit! I'm hit!", "taking fire, breaking off", "he tagged me!", "hit, pulling out", "aagh, I'm hit"],
  burning: ["I'm on fire, bailing out!", "she's burning, going down", "engine's gone, mayday", "punching out, cover me"],
  warncone: ["searchlights on us, scatter!", "we're lit up, break!", "they've got us coned!", "lights! get low!"],
  kill: ["splash one!", "got him! scratch one bandit", "he's going down!", "that's a kill", "shack! good hit"],
  teamdown: ["we lost %N", "%N's down — avenge him", "they got %N", "%N bought it, press on", "cover for %N, he's gone"],
  vengeance: ["that's for last time!", "payback — got my nemesis!", "we're even now", "told you I'd get you"],
  roundwin: ["that's the round — good flying!", "we held the ring! good show", "victory, bring it home", "cracking work, all"],
  roundlose: ["we'll get 'em next round", "regroup, we lost this one", "good fight — chin up", "shake it off, again"],
};

// A little extra colour for named regulars (bot.go's botNames).
const QUIRK = {
  "Stall Warning Sam": { hit: ["I'm hit, I'm hit, oh no—", "mayday! he got me!"], burning: ["I'm going down, I'm going down!"] },
  "Baron von Brick": { kill: ["another for the Baron!", "too easy — splash one!"] },
  "Madame Mayday": { hit: ["mayday, mayday, this is Mayday!"] },
};

export class Radio {
  constructor(hud) {
    this.hud = hud;
    this._counts = {}; // name+kind -> times said, for catchphrase variety
  }

  // Resolve a line and drop it into the team chat log. `sub` fills %N.
  speak(name, team, kind, sub, phraseOverride) {
    let text = phraseOverride;
    if (!text) {
      const list = (QUIRK[name] && QUIRK[name][kind]) || PHRASES[kind];
      if (!list) return;
      const key = name + kind;
      const n = (this._counts[key] = (this._counts[key] ?? 0) + 1);
      text = list[(hash(key) + n) % list.length];
    }
    if (sub) text = text.replace("%N", sub);
    this.hud?.chat?.(name, team, text);
  }

  // Map a game event to chatter. `ctx` supplies the local viewpoint and lookups.
  // ctx = { selfId, selfTeam, spectating, nameOf, teamOf, roster, commsPhrase }
  onEvent(ev, ctx) {
    const mine = (id) => ctx.teamOf(id) === ctx.selfTeam;
    switch (ev.e) {
      case "radio": {
        // Bot internal state (contact/hit/burning/warncone) — your team only, so
        // you're hearing your own squadron, not the enemy's.
        if (!ctx.spectating && !mine(ev.id)) return;
        this.speak(ctx.nameOf(ev.id), ev.team, ev.text);
        break;
      }
      case "kill": {
        // The shooter crows (your side, or a spectator hears everyone); if a
        // wingman of yours fell, someone on the net mourns him.
        if (ev.k && (ctx.spectating || mine(ev.k))) {
          this.speak(ctx.nameOf(ev.k), ctx.teamOf(ev.k), "kill");
        } else if (ev.v && ev.v !== ctx.selfId && (ctx.spectating || ctx.teamOf(ev.v) === ctx.selfTeam)) {
          const mourner = this._teammate(ctx, ev.v, ctx.teamOf(ev.v));
          if (mourner) this.speak(mourner.name, mourner.team, "teamdown", ctx.nameOf(ev.v));
        }
        break;
      }
      case "vengeance": {
        if (!ctx.spectating && !mine(ev.id)) return;
        this.speak(ctx.nameOf(ev.id), ev.team, "vengeance", ctx.nameOf(ev.v));
        break;
      }
      case "comms": {
        // The #17 quick-chat becomes a spoken line for the caller (your team).
        if (!ctx.spectating && ev.team !== ctx.selfTeam) return;
        const label = ctx.commsPhrase?.(ev.plane);
        if (label) this.speak(ctx.nameOf(ev.id), ev.team, "comms", null, label.toLowerCase());
        break;
      }
      case "roundEnd": {
        // A friendly signs off — win or lose from your team's point of view.
        const won = ev.w === ctx.selfTeam;
        const speaker = this._teammate(ctx, 0, ctx.selfTeam);
        if (speaker) this.speak(speaker.name, speaker.team, won ? "roundwin" : "roundlose");
        break;
      }
    }
  }

  // Pick a deterministic teammate to voice a line (not `exclude`, not the local
  // pilot). Returns {name, team} or null.
  _teammate(ctx, exclude, team) {
    const cands = [];
    for (const r of ctx.roster?.values?.() ?? []) {
      if (r.id === exclude || r.id === ctx.selfId || r.team !== team) continue;
      cands.push(r);
    }
    if (!cands.length) return null;
    const r = cands[hash(String(exclude) + team) % cands.length];
    return { name: r.name, team: r.team };
  }
}
